import { TourProvider } from '@talos/kyoko';
import { useMemo, type PropsWithChildren } from 'react';
import { tours, type TourName } from '../tokens/tours';
import { useDisplaySettings } from './DisplaySettingsProvider';
import { useShowcaseContext } from './ShowcaseProvider';

interface TourProviderWrapperProps {
  enable?: boolean;
  tourNames?: TourName[];
}

/**
 * Wrapper around TourProvider that connects it to AppConfig for persistence.
 * This allows tour state to be persisted to the user's AppConfig instead of localStorage.
 */
export function TourProviderWrapper({ children, enable, tourNames }: PropsWithChildren<TourProviderWrapperProps>) {
  // Fetch active showcase and prevent tours from being shown if the showcase is active
  const { currentShowcase } = useShowcaseContext();
  const { seenTours, setSeenTours, firstSeenVersion } = useDisplaySettings();
  const activeTours = useMemo(() => {
    if (tourNames == null) {
      return tours;
    }

    return Object.fromEntries(
      tourNames.map(tourName => [tourName, tours[tourName]]).filter(([, tour]) => tour != null)
    );
  }, [tourNames]);

  return (
    <TourProvider
      tours={activeTours}
      persistedState={seenTours}
      onStateChange={setSeenTours}
      firstSeenVersion={firstSeenVersion}
      enable={enable && currentShowcase == null}
    >
      {children}
    </TourProvider>
  );
}
